import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import Breadcrumb from '@/components/base/Breadcrumb';
import EmptyState from '@/components/base/EmptyState';
import ShareDialog from '@/components/feature/ShareDialog';
import { useFavorites } from '@/hooks/useFavorites';
import { getNewsById, getRelatedNews } from '@/lib/data';
import { buildShareUrl } from '@/lib/share';

export default function NewsDetail() {
  const { id } = useParams<{ id: string }>();
  const item = id ? getNewsById(id) : undefined;
  const { isFavorite, toggleFavorite } = useFavorites();
  const [shareOpen, setShareOpen] = useState(false);
  const [toast, setToast] = useState('');

  useEffect(() => {
    if (item) {
      document.title = `${item.title} - 韩国电影资讯 - 韩国理论片`;
      const desc = document.querySelector('meta[name="description"]');
      if (desc) {
        desc.setAttribute('content', `${item.title}：${item.excerpt}`);
      }
    }
    window.scrollTo(0, 0);
  }, [item]);

  if (!item) {
    return (
      <SiteLayout sidebar>
        <Breadcrumb items={[{ label: '资讯', to: '/news' }, { label: '内容未找到' }]} />
        <EmptyState
          icon="ri-newspaper-line"
          title="没有找到这条资讯"
          description="你访问的资讯可能已下线或链接有误。可以返回资讯列表浏览最新内容。"
        />
        <div className="mt-5 flex justify-center">
          <Link
            to="/news"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-md bg-primary-500 font-label text-sm font-semibold text-background-50 hover:bg-primary-600 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line" aria-hidden="true" />
            返回资讯列表
          </Link>
        </div>
      </SiteLayout>
    );
  }

  const related = getRelatedNews(item, 4);
  const shareUrl = buildShareUrl(`/news/${item.id}`);
  const favorite = isFavorite(`news-${item.id}`);

  const handleFavorite = () => {
    const now = toggleFavorite(`news-${item.id}`);
    setToast(now ? '已收藏该资讯' : '已取消收藏');
    window.setTimeout(() => setToast(''), 2200);
  };

  return (
    <SiteLayout sidebar>
      <Breadcrumb items={[{ label: '资讯', to: '/news' }, { label: item.title }]} />

      <article className="rounded-lg border border-background-200/80 bg-background-50 overflow-hidden">
        <div className="p-5 md:p-8">
          <span className="inline-flex items-center gap-1 rounded-full bg-accent-100 px-3 py-1 font-label text-xs font-semibold text-accent-900">
            {item.category}
          </span>
          <h1 className="mt-3 font-heading text-2xl md:text-3xl font-extrabold leading-snug text-foreground-950">
            {item.title}
          </h1>
          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-foreground-500">
            <span className="inline-flex items-center gap-1">
              <i className="ri-user-3-line" aria-hidden="true" />
              {item.author}
            </span>
            <span className="inline-flex items-center gap-1">
              <i className="ri-calendar-line" aria-hidden="true" />
              {item.date}
            </span>
          </div>
        </div>

        <div className="relative w-full h-56 md:h-80 bg-background-200">
          <img
            src={item.cover}
            alt={`${item.title} 配图`}
            title={item.title}
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
        </div>

        <div className="p-5 md:p-8">
          <div className="flex flex-col gap-4">
            {item.content.map((p, i) => (
              <p key={i} className="text-sm md:text-base leading-loose text-foreground-700">
                {p}
              </p>
            ))}
          </div>

          <div className="mt-8 flex flex-wrap items-center gap-3 border-t border-background-100 pt-6">
            <button
              type="button"
              onClick={handleFavorite}
              className={`inline-flex items-center gap-2 h-11 px-5 rounded-md font-label text-sm font-semibold transition-colors whitespace-nowrap cursor-pointer ${
                favorite
                  ? 'bg-accent-500 text-primary-950 hover:bg-accent-400'
                  : 'bg-primary-500 text-background-50 hover:bg-primary-600'
              }`}
            >
              <i className={favorite ? 'ri-bookmark-fill' : 'ri-bookmark-line'} aria-hidden="true" />
              {favorite ? '已收藏' : '收藏资讯'}
            </button>
            <button
              type="button"
              onClick={() => setShareOpen(true)}
              className="inline-flex items-center gap-2 h-11 px-5 rounded-md border border-background-300 bg-background-50 font-label text-sm font-semibold text-foreground-800 hover:border-primary-300 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-share-line" aria-hidden="true" />
              分享
            </button>
          </div>
        </div>
      </article>

      {related.length > 0 && (
        <section className="mt-12">
          <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
            <i className="ri-links-line text-accent-600" aria-hidden="true" />
            相关阅读
            <span className="font-label text-sm font-medium text-foreground-400">（同分类资讯）</span>
          </h2>
          <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
            {related.map((n) => (
              <Link
                key={n.id}
                to={`/news/${n.id}`}
                className="group flex gap-4 rounded-lg border border-background-200/80 bg-background-50 p-4 hover:border-primary-300 transition-colors cursor-pointer"
              >
                <div className="w-24 h-20 shrink-0 overflow-hidden rounded-md bg-background-200">
                  <img src={n.cover} alt={`${n.title} 配图`} title={n.title} className="w-full h-full object-cover object-top" />
                </div>
                <div className="min-w-0">
                  <h3 className="text-sm font-bold text-foreground-950 group-hover:text-primary-600 transition-colors line-clamp-2">
                    {n.title}
                  </h3>
                  <p className="mt-1.5 text-xs text-foreground-500 line-clamp-2">{n.excerpt}</p>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      <ShareDialog open={shareOpen} onClose={() => setShareOpen(false)} url={shareUrl} title={item.title} />

      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 animate-fade-up">
          <div className="flex items-center gap-2 rounded-full bg-foreground-950 px-4 py-2.5 text-sm text-background-50">
            <i className="ri-checkbox-circle-fill text-accent-400" aria-hidden="true" />
            {toast}
          </div>
        </div>
      )}
    </SiteLayout>
  );
}